import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePreFormWizard } from '@/hooks/submission_forms/usePreFormWizard';
import { useCategoryAutoSelect } from '@/hooks/submission_forms/useCategoryAutoSelect';
import FullPageHeader from '@/components/FullPageHeader';
import LoadingState from '@/components/wizard/LoadingState';
import ErrorState from '@/components/wizard/ErrorState';
import WizardStep from '@/components/wizard/WizardStep';
import ReviewStep from '@/components/wizard/ReviewStep';
import { WizardStepType } from '@/components/wizard/types';
import OrphanSiteWarningBanner from '@/features/submission_forms/components/OrphanSiteWarningBanner';

interface PreFormWizardWithCategoryProps {
  category: string;
  pageTitle: string;
  backPath: string;
  warnIfNoOwner?: boolean;
}

const PreFormWizardWithCategory: React.FC<PreFormWizardWithCategoryProps> = ({
  category,
  pageTitle,
  backPath,
  warnIfNoOwner = false,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ownerId = searchParams.get('owner_id') ?? undefined;

  const {
    currentStepData,
    isLoading,
    error,
    selection,
    owner,
    blockedMessage,
    selectOption,
    previousStep,
    reset,
    initializeForm,
  } = usePreFormWizard();

  const { seeded, categoryError, resetCategorySeed } = useCategoryAutoSelect({
    category,
    isLoading,
    currentStepData,
    blockedMessage,
    selectOption,
  });

  useEffect(() => {
    initializeForm(ownerId);
  }, [initializeForm, ownerId]);

  const handleReset = () => {
    resetCategorySeed();
    reset();
  };

  const handleClose = () => navigate(backPath);

  const handleBack = () => {
    // CATEGORY is locked (auto-selected, hidden) — Back must never land there.
    // previousStep() itself knows whether an earlier, unlocked, non-empty step
    // actually exists (e.g. if SUBCATEGORY had no options for this category,
    // hardcoding "SUBCATEGORY is always the first visible step" would silently
    // strand Back with nowhere to go). When it reports there's nowhere left,
    // fall back to the same destination as the close button.
    if (!previousStep([WizardStepType.CATEGORY])) {
      navigate(backPath);
    }
  };

  const handleRetry = () => {
    resetCategorySeed();
    reset();
    initializeForm(ownerId);
  };

  if (isLoading || !seeded) {
    return <LoadingState message="Loading form options..." />;
  }

  // A blocked message means the owner is lifecycle-ineligible (e.g. not yet Active) —
  // retrying can't fix that, so send the user back instead of offering a false retry.
  if (categoryError && blockedMessage) {
    return (
      <ErrorState
        variant="info"
        message={categoryError}
        onBack={handleClose}
        actionLabel="Go Back"
      />
    );
  }

  if (error || categoryError) {
    return (
      <ErrorState message={categoryError ?? error!} onRetry={handleRetry} />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <FullPageHeader title={pageTitle} onClose={handleClose} />

      {/* Reserve the fixed header's height — see the note in PreFormWizard. */}
      <div className="flex-1 flex flex-col justify-center py-8 pt-[72px]">
        {warnIfNoOwner && !ownerId && (
          <div className="w-6xl mx-auto px-8">
            <OrphanSiteWarningBanner />
          </div>
        )}
        {currentStepData.currentStep === WizardStepType.REVIEW ? (
          <ReviewStep
            selection={selection}
            owner={owner}
            hiddenSteps={[WizardStepType.CATEGORY]}
            onBack={handleBack}
            onReset={handleReset}
          />
        ) : (
          <WizardStep
            currentStepData={currentStepData}
            selection={selection}
            owner={owner}
            hiddenSteps={[WizardStepType.CATEGORY]}
            onSelect={selectOption}
            onBack={handleBack}
            onReset={handleReset}
          />
        )}
      </div>
    </div>
  );
};

export default PreFormWizardWithCategory;
